// 🗺️ 지역썰 음성 프로필 SSOT — 프론트 음성 목록·기본 음색 + generate-tts 억양 지시가 함께 읽는다.
//   지역을 성별·나이로 고정하지 않는다. 추천 화자는 기본값일 뿐이며 유저 선택이 우선.
//
// ★2026-09-04 품질 개정(부산 출신 청취자 «사투리가 너무 이상하다» 피드백)★ — 원인 셋을 함께 고쳤다:
//   ① 지시문이 억제 위주였다. "전국 통용 명료도 유지"·"절제"·"과장 금지"가 넷 중 셋이라 모델이 안전한
//      쪽(=표준 억양으로 사투리 어휘만 읽기)으로 수렴했고, ★성조를 요구하는 문장이 하나도 없었다★.
//      경상권은 높낮이가 뜻을 가르는데 그 축이 지시에서 통째로 빠져 있었다 → prosody 를 권역마다 명시한다.
//   ② 기본 배속이 1.0 이 아니었다(부산 1.08). Gemini 는 speakingRate 가 없어 우리가 PCM 을 직접
//      타임스트레치(WSOLA)하는데, 그 처리가 ★억양 곡선의 시간 구조를 왜곡★한다 — 성조 언어에서 특히
//      티가 난다("배속을 조금만 바꾸면 외국인이 한국어 배운 느낌"이라는 관찰과 일치) → 전 권역 1.0.
//   ③ 억양은 «지시로 흉내내는 연기»라 원어민 성조에 못 미친다. 그래서 ★그 지역 화자로 녹음된 음성★을
//      권역마다 앞자리에 세울 자리(dialectVoiceIds)를 만들었다 — 있으면 그게 추천 1번이 된다.

export type RegionalVoiceProfileId =
  | "capital-standard"
  | "gangwon-inland"
  | "gangwon-east"
  | "chungcheong"
  | "jeonbuk"
  | "jeonnam-west"
  | "jeonnam-east"
  | "gyeongnam-east"
  | "gyeongnam-west"
  | "gyeongbuk-east"
  | "gyeongbuk-north"
  | "jeju";

export interface RegionalVoiceProfile {
  id: RegionalVoiceProfileId;
  label: string;
  /** 프론트 VOICE_CATALOG id. 실제 generate-tts 요청은 이 엔트리의 raw voiceId/vendor 를 쓴다. */
  voiceCatalogId: string;
  defaultTempo: number;
  /** Gemini TTS 전용 Director Notes. EF 가 profile id 화이트리스트로만 주입한다. */
  directorNotes: string;
  /**
   * 🎙 이 권역의 ★특화 화자★ — 그 지역 억양으로 ★녹음된★ 음성(벤더 무관 `VOICE_CATALOG` id).
   * 순서가 곧 추천 순위이고, 있으면 ★목록 맨 앞 + 기본 선택★ 이 된다(회장님 2026-09-04).
   *
   * ★왜 벤더를 안 가리나★: 억양을 «지시»로 시킬 수 있는 벤더는 구글뿐이지만, 애초에 그 지역 사람이
   * 녹음한 음성이면 지시가 필요 없다 — 화자 자체가 사투리다. 그래서 이 목록의 화자는 구글 전용
   * 게이트를 ★건너뛴다★.
   * ⛔ 아무 화자나 넣지 마라 — ★실제로 그 지역 억양으로 들리는지 청취로 확인된 것만★. 이름이
   * 지역스럽다는 이유로 넣으면 «표준 억양으로 사투리를 읽는» 지금 문제가 벤더만 바뀌어 재발한다.
   * 비어 있으면 종전대로 구글 억양 지시 경로만 쓴다(회귀 0).
   */
  dialectVoiceIds?: string[];
}

/** ★배속은 전 권역 1.0 고정★ — 지역 억양은 시간 구조에 실려 있어서, 타임스트레치가 걸리는 순간
 *  성조·장단이 무너진다(위 ② 참조). 유저가 배속을 직접 올리는 것은 그 유저의 선택이지만,
 *  ★기본값이 억양을 깎는 자리★ 는 없앤다. */
const DIALECT_SAFE_TEMPO = 1.0;

/* 🎙 타입캐스트 사투리 화자 — ★벤더 공식 문서 근거★(«릴스 더빙에 딱 맞는 사투리 목소리 TOP3» ·
   «팔도 사투리» 소개글). 우리 카탈로그에 이미 실재하는 세 화자다.
   ★권역 세분은 하지 않는다★ — 벤더 표기가 «경상도»·«전라도» 단위라 부산/대구, 전북/전남을 가를
   근거가 없다. 없는 근거로 쪼개면 «부산인데 대구 화자» 같은 어긋남을 우리가 만드는 셈이다.
   ⛔ 강원·제주·수도권은 비운다 — 그 지역 화자가 없다. 비면 종전대로 구글 억양 지시 경로로 간다. */
const TC_CHUNGCHEONG = "typecast:6059dad0b83880769a50502f"; // 박창수 — 충청
const TC_GYEONGSANG = "typecast:5feb2085cca1a479e73bac37"; // 용식이 — 경상
const TC_JEOLLA = "typecast:606c6c684085209e5555abb0"; // 곽두필 — 전라

const profile = (
  id: RegionalVoiceProfileId,
  label: string,
  voiceCatalogId: string,
  accent: string,
  /** 이 권역의 운율 지시 — «무엇이 이 지역 말을 그 지역 말로 만드는가» 한 줄. */
  prosody: string,
  dialectVoiceIds?: string[],
): RegionalVoiceProfile => ({
  id,
  label,
  voiceCatalogId,
  defaultTempo: DIALECT_SAFE_TEMPO,
  /* ★지시문 설계★: «하지 마라»를 앞세우면 모델이 안전한 표준 억양으로 도망친다. 그래서 ①원어민이라는
     정체성 ②권역별 운율(성조·장단·말끝 곡선)을 ★먼저·구체적으로★ 요구하고, 금지는 마지막 한 줄로
     최소화한다(희화화·비하는 여전히 금지 — 그건 품질이자 예의의 문제다). */
  directorNotes: `### DIRECTOR'S NOTES
Identity: You are a native speaker of ${accent}. This is your everyday speech, not an impression of someone else.
Prosody (most important): ${prosody} Do not flatten the melody into standard Seoul intonation — that is the single most common failure.
Endings: The sentence endings are already written in the Korean transcript. Speak them with the contour a local actually uses, not a standard-Korean reading of the same letters.
Rhythm: Follow local timing — shorten where locals shorten, hold where they hold. Keep consonants clear and pause at sentence boundaries.
Limits: Never caricature, mock, or play it for laughs, and never add regional words that are not in the transcript. Warm and natural, the way someone from there tells a story to a neighbour.`,
  ...(dialectVoiceIds && dialectVoiceIds.length ? { dialectVoiceIds } : {}),
});

export const REGIONAL_VOICE_PROFILES: Record<RegionalVoiceProfileId, RegionalVoiceProfile> = {
  "capital-standard": profile("capital-standard", "수도권 표준", "gemini:Kore",
    "contemporary standard Korean as heard in Seoul and the capital region",
    "Keep the even, level intonation of standard Seoul speech; phrase-final pitch falls gently rather than rising."),
  "gangwon-inland": profile("gangwon-inland", "강원 내륙", "gemini:Callirrhoe",
    "contemporary inland Gangwon Korean, especially the Yeongseo area",
    "Lengthen vowels a little at phrase ends and let the last syllable drift down slowly — an unhurried, drawn-out contour rather than a clipped one."),
  "gangwon-east": profile("gangwon-east", "강원 영동", "gemini:Algenib",
    "contemporary Gangwon east-coast (Yeongdong) Korean",
    "Give sentence ends a slight upward lift and keep vowels open and long; the melody rides higher than standard Korean at the tail."),
  chungcheong: profile("chungcheong", "충청", "gemini:Umbriel",
    "contemporary Chungcheong Korean",
    "Stretch the final syllable noticeably and let the pitch glide down across it — the length itself is the accent. Relaxed tempo inside the phrase, never rushed.",
    [TC_CHUNGCHEONG]),
  jeonbuk: profile("jeonbuk", "전북", "gemini:Sulafat",
    "contemporary North Jeolla Korean",
    "Put a light stress on the phrase-final syllable with a small rise-then-fall, and keep the overall melody wider and more sung than standard Korean.",
    [TC_JEOLLA]),
  "jeonnam-west": profile("jeonnam-west", "전남 서부·광주", "gemini:Achird",
    "contemporary Gwangju and western South Jeolla Korean",
    "Ride a wide, songlike melody: mid-phrase syllables lift and the ending falls off with a soft tail. Vowels stay open and full.",
    [TC_JEOLLA]),
  "jeonnam-east": profile("jeonnam-east", "전남 동부", "gemini:Aoede",
    "contemporary eastern South Jeolla Korean (Yeosu, Suncheon area)",
    "Keep the wide Jeolla melody but a touch quicker and flatter at the tail than Gwangju speech.",
    [TC_JEOLLA]),
  "gyeongnam-east": profile("gyeongnam-east", "경남 동부·부산", "gemini:Puck",
    "contemporary Busan and eastern South Gyeongsang Korean",
    "★This dialect has lexical pitch accent: the height of the syllable carries meaning.★ Start phrases high and drop sharply, put strong pitch peaks on content words, and end statements with a fast, decisive fall. Questions rise steeply on the final syllable. The rhythm is quick and punchy with short, tight vowels — never the even, mid-level melody of Seoul speech.",
    [TC_GYEONGSANG]),
  "gyeongnam-west": profile("gyeongnam-west", "경남 서부·남해안", "gemini:Algieba",
    "contemporary western South Gyeongsang and south-coast Korean",
    "★Pitch accent carries meaning here too★, but the contour is gentler than Busan: high onsets with a rounder fall and slightly longer vowels.",
    [TC_GYEONGSANG]),
  "gyeongbuk-east": profile("gyeongbuk-east", "경북 중동부", "gemini:Charon",
    "contemporary central and eastern North Gyeongsang Korean (Daegu, Yeongcheon, Gyeongju, Pohang)",
    "★Pitch accent carries meaning.★ Peaks sit high and land early in the phrase, and statements end on a firm downward step. The delivery is blunt and clipped rather than melodic.",
    [TC_GYEONGSANG]),
  "gyeongbuk-north": profile("gyeongbuk-north", "경북 북부", "gemini:Gacrux",
    "contemporary northern North Gyeongsang Korean (Andong area)",
    "★Pitch accent carries meaning★, delivered slower and heavier than Daegu speech: high onset, long hold, deliberate fall at the end.",
    [TC_GYEONGSANG]),
  jeju: profile("jeju", "제주", "gemini:Vindemiatrix",
    "contemporary Jeju Korean",
    "Keep a flatter, more level melody than mainland dialects with abrupt, clipped phrase endings; do not add mainland rise-fall patterns."),
};
export const REGIONAL_VOICE_PROFILE_IDS = Object.keys(REGIONAL_VOICE_PROFILES) as RegionalVoiceProfileId[];

export function regionalVoiceProfileById(id: unknown): RegionalVoiceProfile | null {
  if (typeof id !== "string") return null;
  return REGIONAL_VOICE_PROFILES[id as RegionalVoiceProfileId] ?? null;
}
